import React, { useState } from 'react';
import { X, LifeBuoy, CheckCircle, XCircle, Calendar, Clock, Users, GraduationCap, Award } from 'lucide-react';
import { useTrainingData } from '../../hooks/domain/useTrainingData';
import { TRAINING_CATEGORIES } from '../../config/training/categories';
import { normalizeOutcome } from '../../utils/drillHelpers';

const CrewDrillHistoryModal = ({ isOpen, onClose, crewMember, drillParticipation, drills }) => {
  const [activeTab, setActiveTab] = useState('drills');
  const { trainingRecords } = useTrainingData();
  
  if (!isOpen || !crewMember) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get all drills this crew member participated in
  const memberDrills = drills.filter(drill => 
    drill.participants?.some(p => p.id === crewMember.id || p === crewMember.id)
  ).sort((a, b) => new Date(b.date) - new Date(a.date));

  // Group drills by type
  const drillsByType = {};
  memberDrills.forEach(drill => {
    if (!drillsByType[drill.type]) {
      drillsByType[drill.type] = [];
    }
    drillsByType[drill.type].push(drill);
  });
  
  // Get training records for this crew member
  // Support both new format (trainees with crewId) and legacy format (trainee name)
  const memberTraining = trainingRecords.filter(record =>
    record.trainees?.some(t => t.crewId === crewMember.id) ||
    record.trainee === crewMember.full_name ||
    record.trainee === crewMember.name
  ).sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <LifeBuoy className="h-6 w-6 text-cyan-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Drill & Training History</h2>
                <p className="text-sm text-gray-500">{crewMember.full_name} • {crewMember.position}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Compliance Summary */}
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Compliance Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(drillParticipation).map(([drillType, data]) => (
                  <div key={drillType} className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-900">{data.drillName}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {data.lastParticipation 
                            ? `Last: ${formatDate(data.lastParticipation)}`
                            : 'Never participated'}
                        </p>
                        <p className="text-xs text-gray-400">
                          Required every {data.frequency} days
                        </p>
                      </div>
                      {data.isOverdue ? (
                        <span className="inline-flex items-center rounded-full p-1 bg-red-100">
                          <XCircle className="h-4 w-4 text-red-600" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full p-1 bg-green-100">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tab Buttons */}
            <div className="flex border-b mb-4">
              <button
                onClick={() => setActiveTab('drills')}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'drills'
                    ? 'border-b-2 border-cyan-600 text-cyan-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Drills ({memberDrills.length})
              </button>
              <button
                onClick={() => setActiveTab('training')}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'training'
                    ? 'border-b-2 border-cyan-600 text-cyan-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Training ({memberTraining.length})
              </button>
            </div>

            {/* Drill History */}
            {activeTab === 'drills' && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Drill Participation History</h3>
              
              {memberDrills.length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(drillsByType).map(([drillType, typeDrills]) => (
                    <div key={drillType} className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b">
                        <p className="text-sm font-medium text-gray-900">
                          {drillType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-xs text-gray-500">
                          {typeDrills.length} participation{typeDrills.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {typeDrills.slice(0, 5).map((drill) => (
                          <div key={drill.id} className="px-4 py-3 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                  {normalizeOutcome(drill.outcome) === 'successful' ? (
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                  ) : (
                                    <XCircle className="h-5 w-5 text-red-500" />
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-900">
                                      {drill.id || `Drill on ${formatDate(drill.date)}`}
                                    </p>
                                    {normalizeOutcome(drill.outcome) === 'successful' ? (
                                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                        Successful
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                                        Failed
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {formatDate(drill.date)}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {drill.time || 'Time not recorded'}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {drill.participants?.length || 0} participants
                                    </span>
                                  </div>
                                  {drill.scenario && (
                                    <p className="mt-1 text-xs text-gray-600">
                                      Scenario: {drill.scenario}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {typeDrills.length > 5 && (
                          <div className="px-4 py-2 text-xs text-gray-500 text-center bg-gray-50">
                            {typeDrills.length - 5} more earlier participation{typeDrills.length - 5 !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <LifeBuoy className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    No drill participation history found
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    This crew member has not participated in any recorded drills
                  </p>
                </div>
              )}
            </div>
            )}

            {/* Training History */}
            {activeTab === 'training' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Training History</h3>
                
                {memberTraining.length > 0 ? (
                  <div className="space-y-3">
                    {memberTraining.map(record => (
                      <div key={record.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <GraduationCap className="h-5 w-5 text-cyan-600 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{record.topic}</p>
                                <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(record.date)}
                                  </span>
                                  {record.category && (
                                    <span className="text-xs text-gray-400">
                                      {TRAINING_CATEGORIES[record.category]?.label || record.category}
                                    </span>
                                  )}
                                </div>
                                {record.description && (
                                  <p className="mt-2 text-xs text-gray-600">
                                    {record.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right space-y-1">
                            {record.competencyLevel && (
                              <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                                record.competencyLevel === 'proficient' || record.competencyLevel === 'expert'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {record.competencyLevel}
                              </span>
                            )}
                            {record.certificateIssued && (
                              <div className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800">
                                <Award className="h-3 w-3 mr-1" />
                                Certificate
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      No training records found
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      This crew member has not participated in any recorded training
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrewDrillHistoryModal;